import { queryOptions } from '@tanstack/react-query';

import { glacierUsersFeatureFlagsApiGetFeatureFlagsOptions } from '@/lib/heyapi/@tanstack/react-query.gen';
import { glacierUsersFeatureFlagsApiGetFeatureFlags } from '@/lib/heyapi/sdk.gen';

import type { FeatureFlagMap } from './flagNames';

const REFRESH_MS = 1000 * 60 * 1; // ~1 min staleness budget (DC-3338)

// Persist last-known flags so a cold load (e.g. F5) with an unreachable endpoint
// still uses the previous session's values instead of falling back to all-off.
const STORAGE_KEY = 'feature-flags';

function readPersistedFlags(): FeatureFlagMap | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FeatureFlagMap) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Shared query options, built on the generated heyapi query options so the
 * request goes through the configured Hey API client (baseUrl + CF-Access
 * headers + credentials, set in App.tsx) — no hand-rolled fetch. The generated
 * `queryKey` is inherited from the spread; `isFeatureEnabled` reads it via
 * `featureFlagsQueryOptions.queryKey`.
 *
 * We override only what the generated options don't cover:
 * - `queryFn`: reuse the generated SDK call, then persist the result to
 *   localStorage on success (the generated queryFn doesn't).
 * - Graceful degradation (DC-3338 req #4):
 *   - `initialData` seeds the cache from localStorage so a cold load shows the
 *     last-known flags immediately — no flash of all-off — including for the
 *     imperative `isFeatureEnabled` reader, since the seed lands in the cache.
 *   - `initialDataUpdatedAt: 0` marks that seed as stale so we still refetch on
 *     mount to get fresh values.
 *   - On a failed refetch, TanStack Query keeps the last-known data in cache.
 *   - With no cache at all (true first load + endpoint down), consumers default
 *     to `false` (all flags off) — the UI never crashes or blanks.
 */
export const featureFlagsQueryOptions = queryOptions({
  ...glacierUsersFeatureFlagsApiGetFeatureFlagsOptions(),
  queryFn: async () => {
    const { data } = await glacierUsersFeatureFlagsApiGetFeatureFlags({
      throwOnError: true,
    });
    const flags = (data ?? {}) as FeatureFlagMap;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
    } catch {
      // localStorage may be unavailable (private mode / quota) — non-fatal.
    }
    return flags;
  },
  initialData: readPersistedFlags,
  initialDataUpdatedAt: 0,
  staleTime: REFRESH_MS,
  refetchInterval: REFRESH_MS,
  refetchOnWindowFocus: true,
  refetchOnMount: true,
  refetchOnReconnect: true,
});
